import React, { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { Button } from 'ncoded-component-library';
import MymeTypeIcon from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/MymeTypeIcon';
import utils, { getNodeMimeType } from 'utils';
import { DataRoomNodeReport } from '../../../../Reports/types';
import classNames from 'classnames';
import DataRoomNodeTagAndVersion from 'components/DataRoomNodeTagAndVersion';
import SettingsIcon from 'icons/Settings.icon';
import PulseIcon from 'icons/Pulse.icon';

export const FILE_ACTIVITY_COLUMNS = [
  'name',
  'views',
  'timeSpent',
  'manage',
] as const;

export const SORT_NAME_MAP: Record<
  typeof FILE_ACTIVITY_COLUMNS[number],
  SortName | null
> = {
  name: 'sortName',
  views: 'numberOfViews',
  timeSpent: 'numberOfSecondsSpent',
  manage: null,
};

export const COPY_TRANSLATE_MAP = {
  name: 'name',
  views: 'views',
  timeSpent: 'time',
  manage: '',
};

export const POPUP_SORT_NAME: Record<SortName, string> = {
  sortName: '',
  numberOfViews: 'numberOfViewsPopup',
  numberOfSecondsSpent: 'numberOfSecondsSpentPopup',
};

export type SortName = 'sortName' | 'numberOfViews' | 'numberOfSecondsSpent';

export const BODY_COL_CONTENT: Record<
  typeof FILE_ACTIVITY_COLUMNS[number],
  (
    report: DataRoomNodeReport,
    celConf: {
      withManageAccessCell?: boolean;
      hasAnalytics?: boolean;
      isSelected?: boolean;
      analyticsDisabled?: boolean;
      t: TFunction;
      openFile: () => void;
      drawerRefOpen: () => void;
      openAnalytics: () => void;
    },
  ) => ReactNode
> = {
  name: (report, { openFile }) => (
    <div className="td name-cell" key="name">
      <div className="file-name">
        <MymeTypeIcon mimeType={getNodeMimeType(report.dataRoomNode)} />
        <span>
          <span onClick={openFile}>{report.dataRoomNode?.document?.name}</span>

          <DataRoomNodeTagAndVersion
            tag={report.dataRoomNode?.tag}
            version={report.dataRoomNode?.version}
          />
        </span>
      </div>
    </div>
  ),
  views: (report, { isSelected }) => (
    <div className={classNames('td', { selected: isSelected })} key="views">
      <div>{report.numberOfViews}</div>
    </div>
  ),
  timeSpent: (report, { t, isSelected }) => {
    const { type, count } = utils.timeSpentInSecondsHumanize(
      report.numberOfSecondsSpent,
    );
    return (
      <div
        className={classNames('td', { selected: isSelected })}
        key="timeSpent"
      >
        {count === 0
          ? `0m`
          : t(`timeSpentInSecondsHumanizeShort.${type}`, { count })}
      </div>
    );
  },
  manage: (
    _,
    {
      withManageAccessCell,
      hasAnalytics,
      analyticsDisabled,
      drawerRefOpen,
      openAnalytics,
    },
  ) => (
    <div className="manage-cell td" key="manage">
      {hasAnalytics && (
        <Button
          variant="link"
          icon={PulseIcon}
          disabled={analyticsDisabled}
          onClick={openAnalytics}
        />
      )}
      {withManageAccessCell && (
        <Button variant="link" icon={SettingsIcon} onClick={drawerRefOpen} />
      )}
    </div>
  ),
};

export const isSortName = (prop: SortName | null): prop is SortName => !!prop;
